import { JSX, useEffect, useRef } from 'react';
import { FieldPath, UseFormReturn } from 'react-hook-form';

import { LoadRefPillInput } from 'components/input/LoadRefPillInput';
import { RHFCustomerSelect } from 'components/input/RHFCustomerSelect';
import { Grid } from 'components/layout';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { getMcleodIntegrationFromList } from 'pages/QuoteView/LoadBuilding/McleodSectionForms/helpers';
import { NormalizedLoad, TMSCustomer } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';

import { LoadBuildingTextInput } from '../components/LoadBuildingTextInput';

export type CustomerSectionFormProps = {
  formMethods: UseFormReturn<NormalizedLoad>;
  customers: Maybe<TMSCustomer[]>;
  setCustomers: React.Dispatch<React.SetStateAction<Maybe<TMSCustomer[]>>>;
  isLoadingCustomers: boolean;
  setIsLoadingCustomers: React.Dispatch<React.SetStateAction<boolean>>;
  originalSuggestionData?: NormalizedLoad;
  showLoadLevelPo?: boolean;
};

export function CustomerSectionForm({
  formMethods,
  customers,
  setCustomers,
  isLoadingCustomers,
  setIsLoadingCustomers,
  originalSuggestionData,
  showLoadLevelPo = true,
}: CustomerSectionFormProps): JSX.Element {
  const {
    control,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = formMethods;

  const { tmsIntegrations } = useServiceFeatures();
  const mcleodTms = getMcleodIntegrationFromList(tmsIntegrations);

  const watchedCustomer = watch('customer');
  const watchedAddress2 = watch('customer.addressLine2');
  const lastSyncedExternalTMSIDRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const externalTMSID = watchedCustomer?.externalTMSID;
    if (!externalTMSID) {
      lastSyncedExternalTMSIDRef.current = undefined;
      return;
    }

    const selectedCustomer = customers?.find(
      (c) => c.externalTMSID === externalTMSID
    );
    if (!selectedCustomer) {
      return;
    }

    const customerSelectionChanged =
      lastSyncedExternalTMSIDRef.current !== externalTMSID;

    if (customerSelectionChanged) {
      Object.entries(selectedCustomer).forEach(([key, value]) => {
        setValue(`customer.${key}` as FieldPath<NormalizedLoad>, value, {
          shouldDirty: true, // this ensures the AI-label is handled correctly
        });
      });
      lastSyncedExternalTMSIDRef.current = externalTMSID;
      return;
    }

    // TMS list refresh for the same customer: enrich isTest only (id resolved at submit).
    if (selectedCustomer.isTest && !watchedCustomer.isTest) {
      setValue('customer.isTest', true, { shouldDirty: true });
    }
  }, [
    watchedCustomer?.externalTMSID,
    watchedCustomer?.isTest,
    customers,
    setValue,
  ]);

  return (
    <>
      <RHFCustomerSelect
        required
        name={`customer.externalTMSID`}
        label='Name'
        control={control}
        errors={errors}
        customers={customers}
        setCustomers={setCustomers}
        isLoadingCustomers={isLoadingCustomers}
        setIsLoadingCustomers={setIsLoadingCustomers}
        tmsID={mcleodTms?.id}
      />

      <LoadBuildingTextInput
        name='customer.addressLine1'
        label='Address Line 1'
        readOnly={true}
      />

      {!!watchedAddress2 && (
        <LoadBuildingTextInput
          name='customer.addressLine2'
          label='Address Line 2'
          readOnly={true}
        />
      )}
      <LoadBuildingTextInput
        name='customer.city'
        label='City'
        readOnly={true}
      />

      <Grid cols='2' gap='sm' className='w-full m-0'>
        <LoadBuildingTextInput
          name='customer.state'
          label='State'
          readOnly={true}
        />
        <LoadBuildingTextInput
          name='customer.zipCode'
          label='Zip Code'
          placeholder='12345'
          readOnly={true}
        />
      </Grid>

      <LoadRefPillInput
        name='customer.refNumber'
        label='Ref #/ BOL'
        control={control}
        originalSuggestionData={originalSuggestionData || getValues()}
        autoSelectFirstOption={false}
      />

      {showLoadLevelPo && (
        <LoadRefPillInput
          name='poNums'
          label='PO #'
          control={control}
          originalSuggestionData={originalSuggestionData}
          autoSelectFirstOption={false}
        />
      )}
    </>
  );
}
